import { Box, Button, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';
import { ActionBar } from '@/components/Layout';

import { COMMA_PATTERN } from '@/constants/patterns';
import { FUNDING_SOURCE_MAX_LENGTH } from '@/constants/places';
import { getFundingSourceOptions } from '@/constants/user';

import { useAppSelector } from '@/hooks/redux';
import { useQuestionInfoMutation } from '@/hooks/useQuestionInfoMutation';
import { useTranslate } from '@/hooks/useTranslate';

import { selectUserData } from '@/redux/user-data/selectors';

import { CURRENCY_OPTIONS, CurrencyCode } from '@/types/currency';
import { FundingSource } from '@/types/user';

import { getDataTestId } from '@/utils/browser';
import { isCaymanTenant } from '@/utils/tenant';

import {
  formatFundingAmountOnBlur,
  getFundingAmountValidationKey,
} from './QuestionAboutMultipleFundingSources/utils';
import { QuestionProps } from './QuestionDrawer.interface';
import { QuestionForm } from './QuestionForm';

import {
  FundingCard,
  FundingCardInput,
  InlineCurrencySelectWrapper,
} from './QuestionDrawer.styled';

type FormData = {
  fundingSources: string[];
  details: Record<
    string,
    { currency: CurrencyCode; amount: string; description?: string }
  >;
};

export function QuestionAboutMultipleFundingSources({
  onAnswered,
  ...props
}: Readonly<QuestionProps>) {
  const { t } = useTranslate();
  const { user } = useAppSelector(selectUserData);
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors: visibleErrors, isValid },
  } = useForm<FormData>({
    defaultValues: {
      fundingSources: [],
      details: {},
    },
    mode: 'onChange',
  });

  const { mutate, isPending, error } = useQuestionInfoMutation({
    onFinished: onAnswered,
    target: props.pepAnswerTarget,
  });

  const onSubmit = handleSubmit(async payload => {
    const incomeSources = (payload.fundingSources || []).map(sourceType => ({
      sourceType,
      currency: payload.details?.[sourceType]?.currency,
      amount:
        Number.parseFloat(
          (payload.details?.[sourceType]?.amount || '0').replace(
            COMMA_PATTERN,
            '',
          ),
        ) || 0,
      ...(sourceType === FundingSource.OtherIncome
        ? { description: payload.details?.[sourceType]?.description || '' }
        : {}),
    }));

    mutate({ incomeSources });
  });

  const fundingSourceOptions = getFundingSourceOptions(user?.tenant);

  const watchedFundingSources = useWatch({ control, name: 'fundingSources' });
  const selectedSources = watchedFundingSources ?? [];

  const details = useWatch({ control, name: 'details' }) as
    | Record<string, { currency?: string }>
    | undefined;

  const defaultCurrency = isCaymanTenant(user?.tenant) ? 'KYD' : 'USD';

  useEffect(() => {
    selectedSources.forEach(type => {
      const current = details?.[type]?.currency;

      if (!current) {
        setValue(`details.${type}.currency`, defaultCurrency, {
          shouldValidate: true,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFundingSources, details, setValue, defaultCurrency]);

  const renderAmountHelper = (type: string) => {
    if (type === FundingSource.Salary) {
      return t('questionDrawer.aboutFundingSource.amountHelper.salary');
    }
    return t('questionDrawer.aboutFundingSource.amountHelper.other', {
      option: fundingSourceOptions.find(o => o.value === type)?.text || type,
    });
  };

  const validateFundingAmount = (value: unknown) => {
    const result = getFundingAmountValidationKey(value as string, user?.tenant);
    return result === true ? true : t(result);
  };

  const handleAmountBlur = (type: string) => () => {
    if (!isCaymanTenant(user?.tenant)) return;
    const raw = getValues(`details.${type}.amount`) || '';
    const formatted = formatFundingAmountOnBlur(raw);
    if (formatted !== null) {
      setValue(`details.${type}.amount` as const, formatted, {
        shouldValidate: true,
      });
    }
  };

  const CurrencyInlineSelect = useMemo(() => {
    return ({ name }: { name: string }) => (
      <InlineCurrencySelectWrapper>
        <Select
          options={CURRENCY_OPTIONS}
          control={control}
          name={name as never}
          placeholder=""
          defaultValue={defaultCurrency}
          fullWidth={false}
          size="small"
          disabled={isPending}
        />
      </InlineCurrencySelectWrapper>
    );
  }, [control, isPending, defaultCurrency]);

  return (
    <QuestionForm
      {...props}
      isLoading={isPending}
      answerError={error}
      onSubmit={onSubmit}
    >
      <Select
        options={fundingSourceOptions}
        control={control}
        name="fundingSources"
        error={!!visibleErrors.fundingSources}
        helperText={visibleErrors.fundingSources?.message as string}
        rules={{
          required: t(
            'questionDrawer.aboutFundingSource.fundingSource.required',
          ),
        }}
        fullWidth
        multiple
        placeholder={t(
          'questionDrawer.aboutFundingSource.fundingSource.placeHolder',
        )}
        useDrawerOnMobile
        label={t(
          'questionDrawer.aboutFundingSource.fundingSource.label.select',
        )}
        disabled={isPending}
        className="multiSelectItem"
      />

      {selectedSources.map((type: string) => {
        const isOther = type === FundingSource.OtherIncome;
        const optionLabel =
          fundingSourceOptions.find(o => o.value === type)?.text || type;
        return (
          <FundingCard key={type}>
            {isOther && (
              <Box>
                <Typography variant="h5" mb={1}>
                  {optionLabel}
                </Typography>
                <TextInput
                  name={`details.${type}.description` as const}
                  control={control}
                  placeholder={t(
                    'questionDrawer.aboutFundingSource.otherFundingSource.placeHolder',
                  )}
                  error={!!visibleErrors?.details?.[type]?.description}
                  helperText={
                    visibleErrors?.details?.[type]?.description
                      ?.message as string
                  }
                  rules={{
                    required: t(
                      'questionDrawer.aboutFundingSource.otherFundingSource.required',
                    ),
                    maxLength: {
                      value: FUNDING_SOURCE_MAX_LENGTH.otherFundingSource,
                      message: t(
                        'questionDrawer.aboutFundingSource.otherFundingSource.maxLength',
                        {
                          number: FUNDING_SOURCE_MAX_LENGTH.otherFundingSource,
                        },
                      ),
                    },
                  }}
                  disabled={isPending}
                />
              </Box>
            )}

            <Typography variant="body1" mb={1} mt={2}>
              {t('questionDrawer.aboutFundingSource.amountQuestion', {
                option: optionLabel,
              })}
            </Typography>
            <FundingCardInput>
              <TextInput
                name={`details.${type}.amount` as const}
                control={control}
                placeholder={renderAmountHelper(type)}
                error={!!visibleErrors?.details?.[type]?.amount}
                helperText={
                  (visibleErrors?.details?.[type]?.amount
                    ?.message as unknown as string) || undefined
                }
                rules={{
                  required: t(
                    'questionDrawer.aboutFundingSource.fundingSource.required',
                  ),
                  validate: validateFundingAmount,
                }}
                disabled={isPending}
                template="currency"
                startAdornment={
                  <CurrencyInlineSelect name={`details.${type}.currency`} />
                }
                inputMode="numeric"
                autoComplete="off"
                onBlur={handleAmountBlur(type)}
              />
            </FundingCardInput>
          </FundingCard>
        );
      })}
      <ActionBar>
        <Button
          {...getDataTestId(`basic-info-confirm-button`)}
          variant="outlined"
          fullWidth
          type="submit"
          disabled={!isValid || isPending}
        >
          {t('questionDrawer.aboutFundingSource.button.proceed')}
        </Button>
      </ActionBar>
      <FormErrorLogger
        formName="QuestionAboutMultipleFundingSources"
        control={control}
      />
    </QuestionForm>
  );
}
